import { showFatalError } from '../actions/fatal-error-actions';

/**
 * Status codes that should trigger automatic retry with exponential backoff
 * - 403: Cloudflare challenge / Forbidden (temporary)
 * - 408: Request Timeout
 * - 429: Too Many Requests (rate limiting)
 * - 502: Bad Gateway (temporary gateway issues)
 * - 503: Service Unavailable (temporary unavailability)
 * - 504: Gateway Timeout
 */
const RETRIABLE_STATUS_CODES = [403, 408, 429, 502, 503, 504];

/**
 * Configuration for retry behavior
 */
export interface RetryConfig {
    maxRetries?: number;
    baseDelay?: number;
}

/**
 * Enhanced fetch function that automatically retries transient errors with exponential backoff
 * @param input - Request info (URL or Request object)
 * @param init - Fetch initialization options
 * @param retryConfig - Optional retry configuration
 * @param dispatch - Optional Redux dispatch function for showing error modal
 * @returns Promise that resolves with Response
 */
export async function enhancedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
    retryConfig?: RetryConfig,
    dispatch?: (action: any) => void
): Promise<Response> {
    const maxRetries = retryConfig?.maxRetries ?? 3;
    const baseDelay = retryConfig?.baseDelay ?? 1000;
    let retryCount = 0;

    // Preserve the request configuration including body (clone if needed)
    const preservedInit: RequestInit = { ...init };
    
    // If body is a string (most common case), preserve it directly
    // For other types, we'll use it as-is but note that streams/FormData may not work for retries
    if (init?.body && typeof init.body === 'string') {
        preservedInit.body = init.body;
    } else if (init?.body) {
        // For other body types (FormData, Blob, etc.), try to preserve
        // Note: This may not work perfectly for all body types, but preserves the reference
        preservedInit.body = init.body;
    }

    const makeRequest = async (): Promise<Response> => {
        const response = await fetch(input, preservedInit);
        
        if (RETRIABLE_STATUS_CODES.includes(response.status) && retryCount < maxRetries) {
            retryCount++;
            const delay = baseDelay * Math.pow(2, retryCount - 1);
            console.warn(`${response.status} error encountered, retry ${retryCount}/${maxRetries} after ${delay}ms`);
            
            await new Promise(resolve => setTimeout(resolve, delay));
            
            return makeRequest();
        }
        
        if (RETRIABLE_STATUS_CODES.includes(response.status) && retryCount >= maxRetries) {
            console.error(`${response.status} error persisted after ${maxRetries} retries. Showing fatal error message.`);
            
            if (dispatch) {
                dispatch(showFatalError());
            }
        }
        
        return response;
    };

    return makeRequest();
}

